// src/views/ExecPricingView.jsx
/** Exec-only "Custom HW Pricing" view (CONF-106 part 2, dev preview).
 *  Owns NO fetch/permission logic — everything arrives via the `execPricing`
 *  prop (full return of useExecPricing). Session-only: overrides are lost on
 *  refresh by design; see PricingOverrides.ts. */
import React, { useState } from 'react';
import { getProductPrice } from '../utils/configUtilities.jsx';
import { priceSku } from '../pricing/engine';

const fmtMoney = (n) =>
  `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const parseMoney = (raw) => {
  const n = parseFloat(String(raw).replace(/[$,]/g, ''));
  return Number.isNaN(n) ? null : n;
};

// Uplift-exempt components (kind labor/chassis — JBOD chassis, node labor)
// are never editable from this view; base remainders are catalog-internal.
// Exemption is kind-driven via the snapshot's exempt set, so a materials-kind
// chassis (uplifted, e.g. a node's sheet metal) stays editable. CMP-BASE-*
// stays a prefix match until a kind-carrying snapshot lands in part 3.
const isReadOnlyComponent = (componentSku, upliftExemptSkus) =>
  componentSku.startsWith('CMP-BASE-') || upliftExemptSkus.has(componentSku);

// Display order: Node → NVMe → JBOD → NIC → everything else.
const CATEGORY_TESTS = [
  (sku) => /-[DSF]\d*N$/.test(sku),   // director/storage/flash nodes
  (sku) => /^VCH-NVME-/.test(sku),    // NVMe drives
  (sku) => /-J\d+-\d+$/.test(sku),    // JBODs (bays-capacity name math)
  (sku) => /NDR\d+/.test(sku),        // NICs
];
const categoryRank = (sku) => {
  const i = CATEGORY_TESTS.findIndex((test) => test(sku));
  return i === -1 ? CATEGORY_TESTS.length : i;
};
const skuCompare = (a, b) =>
  categoryRank(a) - categoryRank(b) || a.localeCompare(b, undefined, { numeric: true });

const catalogPriceDollars = (sku, snapshot) => {
  try {
    return priceSku(sku, snapshot).discountCents / 100;
  } catch (e) {
    // Missing cost/composition = expected partial-catalog state -> em-dash.
    // Anything else is a real engine fault and must surface, not vanish.
    if (/missing component cost|no composition|no discount %/.test(String(e?.message))) return null;
    throw e;
  }
};

// -- Integration % chip + editor card (mockup option-K pattern) -------------
function IntegrationChip({
  snapshot, overrides, hwLineCount, isOpen, onToggle, error, onChange, onCommit, onReset,
}) {
  const overrideBp = overrides?.integrationBp;
  const hasOverride = overrideBp !== null && overrideBp !== undefined;
  const effectivePct = (hasOverride ? overrideBp : snapshot.integrationBp) / 100;
  const basePct = snapshot.integrationBp / 100;

  return (
    <div className="mb-4">
      <button
        type="button"
        aria-label="Edit integration percent"
        aria-pressed={isOpen}
        onClick={onToggle}
        className={`inline-flex items-center gap-2 rounded-full border bg-gray-900 px-3 py-1 text-sm hover:border-orange-400 ${
          hasOverride ? 'border-red-500' : 'border-gray-600'}`}
      >
        <span className="text-orange-400">✎</span>
        <span className="text-gray-200">Integration %</span>
        <span className={hasOverride ? 'text-red-500 font-bold' : 'font-semibold text-gray-200'}>
          {effectivePct.toFixed(2)}%
        </span>
      </button>

      {isOpen && (
        <div className="mt-2 max-w-md rounded border border-gray-700 bg-gray-900/40 p-3">
          <div className="mb-2 flex items-baseline justify-between">
            <span className="font-semibold text-gray-200">Integration %</span>
            <span className="text-xs text-gray-400">
              current <span className="font-semibold text-gray-200">{basePct.toFixed(1)}%</span>
            </span>
          </div>
          <div className="mb-2 rounded border border-yellow-700 bg-yellow-900/30 px-3 py-2 text-sm text-yellow-300">
            Changing the integration % reprices ALL hardware lines.
          </div>
          <div className="flex flex-wrap items-end gap-4">
            <label>
              <span className="mb-1 block text-[11px] uppercase tracking-wide text-gray-400">
                New integration % (min 1)
              </span>
              {/* Keyed on the override so a commit/reset remounts this uncontrolled
                  input with the freshly-effective default, even while left open. */}
              <input
                key={`int-${hasOverride ? overrideBp : 'base'}`}
                type="text"
                inputMode="decimal"
                aria-label="New integration percent"
                defaultValue={effectivePct.toString()}
                onChange={onChange}
                onBlur={onCommit}
                onKeyDown={(e) => { if (e.key === 'Enter') onCommit(); }}
                className="w-28 rounded border border-gray-700 bg-gray-900 px-2 py-1 text-right text-white"
              />
            </label>
            <div>
              <span className="mb-1 block text-[11px] uppercase tracking-wide text-gray-400">
                applies to
              </span>
              <span className="font-semibold text-gray-200">
                {hwLineCount} HW line{hwLineCount === 1 ? '' : 's'}
              </span>
            </div>
            <button
              type="button"
              aria-label="Reset integration percent"
              onClick={onReset}
              className="ml-auto rounded border border-gray-600 px-2 py-1 text-xs hover:bg-gray-800"
            >
              reset
            </button>
          </div>
          {error && <div className="mt-1 text-sm text-red-500">{error}</div>}
        </div>
      )}
    </div>
  );
}

// -- Per-SKU component editor (one-at-a-time, rendered inline under a row) --
function ComponentEditor({
  sku, composition, componentCostMicros, upliftExemptSkus, componentOverrides,
  onResetRow, onSetComponentCost, onCommitComponent, onDraftChange,
}) {
  return (
    <tr>
      <td className="p-2"></td>
      <td colSpan={4} className="p-2 bg-gray-900/40">
        <div className="flex justify-between items-center mb-2">
          <span className="text-xs text-gray-400 uppercase tracking-wide">Components</span>
          <button
            type="button"
            aria-label={`Reset ${sku} row`}
            onClick={onResetRow}
            className="rounded border border-gray-600 px-2 py-1 text-xs hover:bg-gray-800"
          >
            reset
          </button>
        </div>
        <table className="text-xs">
          <tbody>
            {composition.map(({ componentSku }) => {
              const baseMicros = componentCostMicros[componentSku];
              const overrideMicros = componentOverrides?.[componentSku];
              const hasOverride = overrideMicros !== undefined;
              const readOnly = isReadOnlyComponent(componentSku, upliftExemptSkus);
              const baseCost = baseMicros !== undefined ? baseMicros / 1e6 : null;
              const defaultCost = hasOverride ? overrideMicros / 1e6 : baseCost;

              return (
                <tr key={componentSku} className="border-t border-gray-800">
                  <td className="py-1 pr-4">{componentSku}</td>
                  <td className="py-1 pr-4 text-right">{baseCost != null ? fmtMoney(baseCost) : '—'}</td>
                  <td className="py-1">
                    {readOnly ? (
                      <span className="text-gray-500">read-only</span>
                    ) : (
                      <>
                        {/* Keyed on the active override so a commit/reset (which
                            changes componentOverrides upstream) remounts this
                            uncontrolled input with the new default. */}
                        <input
                          key={`${sku}-${componentSku}-${hasOverride ? overrideMicros : 'base'}`}
                          type="text"
                          aria-label={`New ${componentSku} cost`}
                          defaultValue={defaultCost != null ? defaultCost.toFixed(2) : ''}
                          onChange={(e) => onDraftChange(sku, componentSku, e.target.value)}
                          onBlur={() => onCommitComponent(sku, componentSku)}
                          onKeyDown={(e) => { if (e.key === 'Enter') onCommitComponent(sku, componentSku); }}
                          className="bg-gray-900 text-white border border-gray-700 px-1 py-0.5 w-24"
                        />
                        <button
                          type="button"
                          aria-label={`Reset ${componentSku} cost`}
                          onClick={() => onSetComponentCost(sku, componentSku, null)}
                          className="ml-1 text-gray-400 hover:text-white"
                        >
                          ↺
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </td>
    </tr>
  );
}

// -- One catalog row (+ its notes + its expanded component editor) ---------
function CatalogRow({
  sku, snapshot, productDb, config, activePartNumbers,
  customPrices, customSkus, overrides, catalogDollars,
  editedComponents,
  isOpen, onToggleOpen,
  onResetRow, onSetComponentCost, onCommitComponent, onDraftChange,
}) {
  const hasComposition = !!snapshot.compositions[sku];
  const inConfig = activePartNumbers.has(sku);
  const description = productDb?.products?.[sku]?.description || '';
  const rowGreyed = !inConfig ? 'opacity-40' : '';
  const inProductDb = !!productDb?.products?.[sku];

  // catalogDollars is precomputed by the parent (see ExecPricingView) so a
  // genuine engine fault throws during that plain-JS precompute pass — where
  // it can actually be caught — rather than here, deep in child rendering.
  const newPrice = (() => {
    if (customPrices[sku] !== undefined) return { dollars: customPrices[sku], custom: true };
    if (inProductDb) return { dollars: getProductPrice(productDb, config.quarter, sku), custom: false };
    return { dollars: catalogDollars, custom: false };
  })();

  // Unknown SKUs with no cost breakdown and no productDb entry have no price
  // to show at all — getProductPrice() returns 0 for unknown SKUs, which is
  // NOT the same as "no price" here, so the productDb lookup is gated.
  const currentDollars = hasComposition
    ? catalogDollars
    : (inProductDb ? getProductPrice(productDb, config.quarter, sku) : null);

  const editCount = editedComponents.length;
  // Tooltip names the components; long lists truncate so the title stays readable.
  const editTitle = editedComponents.slice(0, 4).join(', ')
    + (editCount > 4 ? ` +${editCount - 4} more` : '');
  const marked = hasComposition && editCount > 0;

  return (
    <React.Fragment>
      <tr className={`border-t border-gray-800 ${rowGreyed}`}>
        {/* Gutter bar (VS Code settings / IntelliJ / git-diff idiom): a row whose
            component costs were edited by hand carries a mark at a FIXED left
            position, so the marked rows form a vertical scan line. Deliberately
            neutral — red means "custom price" and orange means "editable", both
            already spoken for; a third saturated hue would collapse under
            deuteranopia and say nothing colour wasn't already saying. */}
        <td className="relative p-2">
          {marked && (
            <>
              <span
                aria-hidden="true"
                title={editTitle}
                className="absolute inset-y-1 left-0 w-[3px] rounded-sm bg-gray-300"
              />
              {/* The bar is decorative; this carries the same fact as text. */}
              <span className="sr-only">
                {`${editCount} component cost${editCount === 1 ? '' : 's'} edited on ${sku}: ${editTitle}`}
              </span>
            </>
          )}
          {hasComposition && (
            <button
              type="button"
              aria-label={`Edit ${sku}`}
              onClick={onToggleOpen}
              className="text-orange-400 hover:text-orange-300"
            >
              ✎
            </button>
          )}
        </td>
        <td className="p-2">{sku}</td>
        <td className="p-2 text-gray-400">{description}</td>
        <td className="p-2">{currentDollars != null ? fmtMoney(currentDollars) : '—'}</td>
        <td className="p-2">
          {newPrice.dollars != null ? (
            <span className={newPrice.custom ? 'text-red-500 font-bold' : ''}>{fmtMoney(newPrice.dollars)}</span>
          ) : '—'}
        </td>
      </tr>

      {!hasComposition && (
        <tr className={rowGreyed}>
          <td className="p-2"></td>
          <td colSpan={4} className="p-2 pt-0 text-gray-500 italic text-xs">
            no cost breakdown yet
          </td>
        </tr>
      )}

      {!inConfig && customSkus.has(sku) && (
        <tr className="opacity-40">
          <td className="p-2"></td>
          <td colSpan={4} className="p-2 pt-0 text-gray-500 italic text-xs">
            applies if added
          </td>
        </tr>
      )}

      {isOpen && hasComposition && (
        <ComponentEditor
          sku={sku}
          composition={snapshot.compositions[sku]}
          componentCostMicros={snapshot.componentCostMicros}
          upliftExemptSkus={snapshot.upliftExemptSkus}
          componentOverrides={overrides?.componentCosts?.[sku]}
          onResetRow={onResetRow}
          onSetComponentCost={onSetComponentCost}
          onCommitComponent={onCommitComponent}
          onDraftChange={onDraftChange}
        />
      )}
    </React.Fragment>
  );
}

// -- Footer ------------------------------------------------------------------
// The two override states are separated in TEXT here, not by colour alone:
// red on a price says "custom", but says nothing about which of the two causes
// produced it. This line is what makes that distinction readable (and audible).
function ViewFooter({ editCount, editedSkuCount, integrationPct, baseIntegrationPct, onResetAll }) {
  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-gray-800 pt-3">
      <button
        type="button"
        onClick={onResetAll}
        className="px-3 py-1 rounded border border-gray-600 hover:bg-gray-800"
      >
        Reset all
      </button>
      <div className="text-sm text-gray-400">
        {editedSkuCount === 0
          ? 'no manual edits'
          : `${editCount} edit${editCount === 1 ? '' : 's'} across ${editedSkuCount} SKU${editedSkuCount === 1 ? '' : 's'}`}
        {integrationPct !== null && (
          <>
            {' · '}integration{' '}
            <span className="font-semibold text-red-500">{integrationPct.toFixed(2)}%</span>
            {` (was ${baseIntegrationPct.toFixed(2)}%)`}
          </>
        )}
      </div>
      <div className="text-xs text-gray-500 italic">
        dev preview — custom pricing is session-only
      </div>
    </div>
  );
}

// -- Main view -----------------------------------------------------------------
const ExecPricingView = ({
  execPricing, productDb, config, activePartNumbers, onReprice,
}) => {
  const {
    status, snapshot, overrides, customPrices, customSkus, changeCount, editionDrifted,
    setComponentCost, setIntegrationPct, resetSku, resetAll, retry,
  } = execPricing;

  const [openSku, setOpenSku] = useState(null);
  const [filter, setFilter] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [intError, setIntError] = useState(null);
  const [draft, setDraft] = useState({});

  const setDraftValue = (key, value) => setDraft((d) => ({ ...d, [key]: value }));
  const onDraftChange = (productSku, componentSku, value) =>
    setDraftValue(`${productSku}::${componentSku}`, value);

  const commitComponent = (productSku, componentSku) => {
    const raw = draft[`${productSku}::${componentSku}`];
    if (raw === undefined) return;
    const dollars = parseMoney(raw);
    if (dollars === null) return;
    setComponentCost(productSku, componentSku, dollars);
  };

  const commitIntegration = () => {
    const raw = draft.__int;
    if (raw === undefined) return;
    const pct = parseMoney(raw);
    if (pct === null) return;
    try {
      setIntegrationPct(pct);
      setIntError(null);
    } catch (e) {
      setIntError(e.message);
    }
  };

  const resetIntegration = () => {
    setIntegrationPct(null);
    setIntError(null);
    setDraft((d) => {
      const { __int, ...rest } = d;
      return rest;
    });
  };

  // Framed inset: the BOM table below uses the shadcn Table primitives and a
  // different visual language. Framing (rather than restyling this table to
  // match) keeps this table's density — which is doing real work at 40+ SKUs —
  // and makes the difference read as "tool vs output" instead of a mismatch.
  const frame = (children) => (
    <div className="mb-6">
      <div className="mb-2 text-xs uppercase tracking-wide text-gray-400">
        Custom hardware pricing — session overrides
      </div>
      <div className="rounded-lg border border-gray-800 bg-gray-900/20 p-4">{children}</div>
    </div>
  );

  if (status === 'loading') {
    return frame(<div className="text-gray-400">Loading custom hardware pricing…</div>);
  }

  if (status === 'error' || !snapshot) {
    return frame(
      <>
        <div className="text-red-400 mb-2">Failed to load catalog pricing.</div>
        <button
          type="button"
          onClick={retry}
          className="px-3 py-1 rounded border border-gray-600 hover:bg-gray-800"
        >
          Retry
        </button>
      </>,
    );
  }

  const compositionSkus = Object.keys(snapshot.compositions);
  const extraActiveSkus = [...activePartNumbers].filter(
    (pn) => pn.startsWith('VCH-') && !snapshot.compositions[pn],
  );
  // Default scope: SKUs in the current config, plus any SKU carrying a
  // per-component override (an edit must never vanish from view when the
  // config changes). Deliberately NOT customSkus: an integration-% override
  // marks EVERY composition SKU custom, which would force full-catalog view.
  const editedComponentsOf = (sku) => Object.keys(overrides?.componentCosts?.[sku] ?? {});
  const inScope = (sku) => showAll || activePartNumbers.has(sku) || editedComponentsOf(sku).length > 0;
  const allSkus = [...compositionSkus, ...extraActiveSkus]
    .filter(inScope)
    .filter((sku) => !filter || sku.toLowerCase().includes(filter.toLowerCase()))
    .sort(skuCompare);

  const hwLineCount = compositionSkus.filter((sku) => activePartNumbers.has(sku)).length;

  const editedSkus = Object.keys(overrides?.componentCosts ?? {})
    .filter((sku) => editedComponentsOf(sku).length > 0);
  const totalEditCount = editedSkus.reduce((n, sku) => n + editedComponentsOf(sku).length, 0);
  const overrideIntegrationPct = overrides?.integrationBp == null ? null : overrides.integrationBp / 100;

  // Precompute catalog prices for every visible SKU in plain JS, BEFORE the
  // JSX tree is built. A thrown engine fault is only catchable here — once a
  // <CatalogRow/> element is handed to React, its function body runs during
  // reconciliation, outside any try/catch we could write in this component.
  let catalogDollarsBySku;
  let engineError = null;
  try {
    catalogDollarsBySku = {};
    for (const sku of allSkus) {
      if (snapshot.compositions[sku]) catalogDollarsBySku[sku] = catalogPriceDollars(sku, snapshot);
    }
  } catch (e) {
    engineError = e;
  }

  if (engineError) {
    return frame(
      <>
        <div className="text-red-400 mb-2">Pricing engine error: {engineError.message}</div>
        <button
          type="button"
          onClick={retry}
          className="px-3 py-1 rounded border border-gray-600 hover:bg-gray-800"
        >
          Retry
        </button>
      </>,
    );
  }

  return frame(
    <>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input
          type="text"
          aria-label="Filter catalog"
          placeholder="Filter by part number…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="bg-gray-900 text-white border border-gray-700 px-2 py-1 w-full max-w-sm"
        />
        <button
          type="button"
          aria-pressed={showAll}
          onClick={() => setShowAll((v) => !v)}
          className={`px-3 py-1 rounded border text-sm ${
            showAll ? 'border-orange-400 text-orange-300' : 'border-gray-600 text-gray-300'} hover:bg-gray-800`}
        >
          Show full catalog
        </button>
        {changeCount > 0 && (
          <button
            type="button"
            onClick={onReprice}
            title={editionDrifted
              ? 'The published catalog has changed since this session started — overrides are held steady until you reprice.'
              : undefined}
            className={`px-3 py-1 rounded border text-sm ${editionDrifted
              ? 'border-red-500 text-red-300 hover:bg-red-900/30 animate-pulse'
              : 'border-amber-500 text-amber-300 hover:bg-amber-900/30'}`}
          >
            {editionDrifted ? 'Catalog updated — reprice to sync' : 'Reprice vs current catalog'}
          </button>
        )}
      </div>

      <IntegrationChip
        snapshot={snapshot}
        overrides={overrides}
        hwLineCount={hwLineCount}
        isOpen={openSku === '__int'}
        onToggle={() => { setOpenSku(openSku === '__int' ? null : '__int'); setIntError(null); }}
        error={intError}
        onChange={(e) => setDraftValue('__int', e.target.value)}
        onCommit={commitIntegration}
        onReset={resetIntegration}
      />

      {/* Catalog table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-orange-400 text-left">
              <th className="p-2"></th>
              <th className="p-2">P/N</th>
              <th className="p-2">Description</th>
              <th className="p-2">{`Current price (${config.quarter})`}</th>
              <th className="p-2">New price</th>
            </tr>
          </thead>
          <tbody>
            {allSkus.map((sku) => (
              <CatalogRow
                key={sku}
                sku={sku}
                snapshot={snapshot}
                productDb={productDb}
                config={config}
                activePartNumbers={activePartNumbers}
                customPrices={customPrices}
                customSkus={customSkus}
                overrides={overrides}
                catalogDollars={catalogDollarsBySku[sku]}
                editedComponents={editedComponentsOf(sku)}
                isOpen={openSku === sku}
                onToggleOpen={() => setOpenSku(openSku === sku ? null : sku)}
                onResetRow={() => resetSku(sku)}
                onSetComponentCost={setComponentCost}
                onCommitComponent={commitComponent}
                onDraftChange={onDraftChange}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Only shown once a bar is actually on screen — a legend for a mark that
          isn't there is noise, and the footer already states the counts. */}
      {totalEditCount > 0 && (
        <div className="mt-2 flex items-center gap-2 px-2 text-[11px] text-gray-500">
          <span aria-hidden="true" className="inline-block h-3 w-[3px] rounded-sm bg-gray-300" />
          component overrides
        </div>
      )}

      <ViewFooter
        editCount={totalEditCount}
        editedSkuCount={editedSkus.length}
        integrationPct={overrideIntegrationPct}
        baseIntegrationPct={snapshot.integrationBp / 100}
        onResetAll={resetAll}
      />
    </>,
  );
};

export default ExecPricingView;
